import type { BedrockMcpQueryAnalyticsOutput } from "schemas/generated/agentTools/replo/bedrockMcp";
import type { RequestFn } from "./request";

import { analyticsPixelStatusResponseSchema } from "schemas/bedrockApi/analyticsPixel";
import { analyticsSiteDomainsResponseSchema } from "schemas/bedrockApi/analyticsSiteDomains";

// NOTE (Noah, 2026-05-06): Mirrors the `query_replo_analytics` MCP tool — raw
// ClickHouse SQL passthrough scoped to `projectId` via per-project roles + RLS.
// Cross-project access (admin debugging) is expressed by passing a different
// `projectId`; the bedrock-api access check allows that only for superusers.
// See the `querying-analytics` skill for table schemas. Errors surface as
// BedrockApiRequestError via the standard SDK error path; we don't adopt the
// MCP's "errors-as-content" envelope.
export function createAnalyticsClient({ request }: { request: RequestFn }) {
  return {
    query: ({ projectId, query }: { projectId: string; query: string }) => {
      return request<BedrockMcpQueryAnalyticsOutput>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/analytics/query`,
        body: { query },
      });
    },
    // The workspace's storefront domains, split into the project's own and
    // every workspace domain, for the Insights site-domain filter.
    siteDomains: async ({ projectId }: { projectId: string }) => {
      const result = await request<unknown>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/analytics/site-domains`,
      });
      return analyticsSiteDomainsResponseSchema.parse(result);
    },
    // The tag to paste on a site outside Replo, and whether it has sent a visit yet.
    pixelStatus: async ({ projectId }: { projectId: string }) => {
      const result = await request<unknown>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/analytics/pixel`,
      });
      return analyticsPixelStatusResponseSchema.parse(result);
    },
  };
}
